import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
// Removed lucide-react icons, using Voice logo instead
import { useVoice } from '../contexts/VoiceContext'
import { useWeb3Wallet } from '../contexts/Web3WalletProvider'
import { useTransaction } from '../contexts/TransactionContext'
import { getSupportedLanguages } from '../services/speechToText'
import { getAvailableVoices } from '../services/textToSpeech'

interface SettingSection {
  id: string
  title: string
  icon: React.ReactNode
  description: string
}

const SettingsPage: React.FC = () => {
  const { language, setLanguage, clearErrors } = useVoice()
  const { isConnected, disconnect, address: walletAddress } = useWeb3Wallet()
  const { exportTransactions, importTransactions, clearTransactions } = useTransaction()
  
  const [activeSection, setActiveSection] = useState<string>('voice')
  const [darkMode, setDarkMode] = useState(true)
  const [notifications, setNotifications] = useState(true)
  const [biometrics, setBiometrics] = useState(false)
  const [autoSpeak, setAutoSpeak] = useState(true)
  const [voiceRate, setVoiceRate] = useState(1)
  const [voicePitch, setVoicePitch] = useState(1)
  const [voiceVolume, setVoiceVolume] = useState(1)
  const [selectedVoice, setSelectedVoice] = useState('')
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([])
  const [supportedLanguages] = useState(getSupportedLanguages())
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [showSuccess, setShowSuccess] = useState(false)

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('voicePaySettings')
    if (savedSettings) {
      const settings = JSON.parse(savedSettings)
      setDarkMode(settings.darkMode ?? true)
      setNotifications(settings.notifications ?? true)
      setBiometrics(settings.biometrics ?? false)
      setAutoSpeak(settings.autoSpeak ?? true)
      setVoiceRate(settings.voiceRate ?? 1)
      setVoicePitch(settings.voicePitch ?? 1)
      setVoiceVolume(settings.voiceVolume ?? 1)
      setSelectedVoice(settings.selectedVoice ?? '')
    }

    // Get available voices
    const voices = getAvailableVoices()
    setAvailableVoices(voices)

    // Listen for online/offline status
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const saveSettings = () => {
    const settings = {
      darkMode,
      notifications,
      biometrics,
      autoSpeak,
      voiceRate,
      voicePitch,
      voiceVolume,
      selectedVoice
    }
    localStorage.setItem('voicePaySettings', JSON.stringify(settings))
    setShowSuccess(true)
    setTimeout(() => setShowSuccess(false), 2000)
  }

  const resetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings to default?')) {
      localStorage.removeItem('voicePaySettings')
      setDarkMode(true)
      setNotifications(true)
      setBiometrics(false)
      setAutoSpeak(true)
      setVoiceRate(1)
      setVoicePitch(1)
      setVoiceVolume(1)
      setSelectedVoice('')
      setLanguage('en-US')
    }
  }

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string)
          importTransactions(data)
          setShowSuccess(true)
          setTimeout(() => setShowSuccess(false), 2000)
        } catch (error) {
          alert('Invalid file format')
        }
      }
      reader.readAsText(file)
    }
  }

  const sections: SettingSection[] = [
    {
      id: 'voice',
      title: 'Voice & Speech',
      icon: <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />,
      description: 'Configure voice recognition and speech settings'
    },
    {
      id: 'security',
      title: 'Security',
      icon: <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />,
      description: 'Manage security and privacy settings'
    },
    {
      id: 'data',
      title: 'Data & Storage',
      icon: <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />,
      description: 'Export, import, and manage your data'
    },
    {
      id: 'app',
      title: 'App Settings',
      icon: <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />,
      description: 'General application preferences'
    }
  ]

  const formatAddress = (address: string | null) => {
    if (!address) return 'Not connected'
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-8">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 rounded-xl bg-yellow-500">
              <img src="/Voice-32x32.svg" alt="Voice" className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">
                Settings
              </h1>
              <p className="text-white/70">
                Customize your Voice AI Payment Assistant
              </p>
            </div>
          </div>

          {/* Connection Status */}
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
              isOnline ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
            }`}>
              <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
              <span>{isOnline ? 'Online' : 'Offline'}</span>
            </div>
            
            {isConnected && (
              <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm">
                <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
                <span>Wallet: {formatAddress(walletAddress)}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Success Message */}
        {showSuccess && (
          <motion.div
            className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center space-x-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />
            <span className="text-green-200">Settings saved successfully!</span>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="glass rounded-xl border border-white/20 p-4">
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                      activeSection === section.id
                        ? 'bg-white/20 text-white'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {section.icon}
                      <div>
                        <div className="font-medium">{section.title}</div>
                        <div className="text-xs text-white/50 mt-1">
                          {section.description}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="glass rounded-xl border border-white/20 p-6">
              {/* Voice & Speech Settings */}
              {activeSection === 'voice' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-white mb-4">
                    Voice & Speech Settings
                  </h2>

                  {/* Language Selection */}
                  <div>
                    <label className="block text-white font-medium mb-2">
                      <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4 inline mr-2" />
                      Voice Recognition Language
                    </label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="w-full glass rounded-lg border border-white/20 bg-gray-800 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      style={{ backgroundColor: '#1f2937', color: '#ffffff' }}
                    >
                      {supportedLanguages.map((lang) => (
                        <option key={lang.code} value={lang.code} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Voice Selection */}
                  <div>
                    <label className="block text-white font-medium mb-2">
                      <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4 inline mr-2" />
                      Text-to-Speech Voice
                    </label>
                    <select
                      value={selectedVoice}
                      onChange={(e) => setSelectedVoice(e.target.value)}
                      className="w-full glass rounded-lg border border-white/20 bg-gray-800 text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      style={{ backgroundColor: '#1f2937', color: '#ffffff' }}
                    >
                      <option value="" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Default Voice</option>
                      {availableVoices.map((voice, index) => (
                        <option key={index} value={voice.name} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                          {voice.name} ({voice.lang})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Voice Controls */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Speech Rate: {voiceRate.toFixed(1)}
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={voiceRate}
                        onChange={(e) => setVoiceRate(parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Pitch: {voicePitch.toFixed(1)}
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={voicePitch}
                        onChange={(e) => setVoicePitch(parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-medium mb-2">
                        Volume: {voiceVolume.toFixed(1)}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={voiceVolume}
                        onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Auto Speak */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">Auto-speak responses</div>
                      <div className="text-white/70 text-sm">Automatically speak AI responses</div>
                    </div>
                    <button
                      onClick={() => setAutoSpeak(!autoSpeak)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        autoSpeak ? 'bg-blue-500' : 'bg-gray-600'
                      }`}
                    >
                      <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
                        autoSpeak ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                </div>
              )}

              {/* Security Settings */}
              {activeSection === 'security' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-white mb-4">
                    Security Settings
                  </h2>

                  {/* Wallet Connection */}
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <img src="/Voice-32x32.svg" alt="Voice" className="w-5 h-5" />
                        <div>
                          <div className="text-white font-medium">Wallet Connection</div>
                          <div className="text-white/70 text-sm">
                            {isConnected ? `Connected: ${formatAddress(walletAddress)}` : 'No wallet connected'}
                          </div>
                        </div>
                      </div>
                      {isConnected && (
                        <button
                          onClick={disconnect}
                          className="px-3 py-1 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors text-sm"
                        >
                          Disconnect
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Biometric Authentication */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium flex items-center space-x-2">
                        <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
                        <span>Biometric Authentication</span>
                      </div>
                      <div className="text-white/70 text-sm">Use fingerprint or face unlock</div>
                    </div>
                    <button
                      onClick={() => setBiometrics(!biometrics)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        biometrics ? 'bg-blue-500' : 'bg-gray-600'
                      }`}
                    >
                      <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
                        biometrics ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  {/* Clear Voice Errors */}
                  <div>
                    <button
                      onClick={clearErrors}
                      className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-colors"
                    >
                      <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
                      <span>Clear Voice Recognition Errors</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Data & Storage */}
              {activeSection === 'data' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-white mb-4">
                    Data & Storage
                  </h2>

                  {/* Export Data */}
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium flex items-center space-x-2">
                          <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
                          <span>Export Transaction History</span>
                        </div>
                        <div className="text-white/70 text-sm">Download your transaction data as JSON</div>
                      </div>
                      <button
                        onClick={exportTransactions}
                        className="px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                      >
                        Export
                      </button>
                    </div>
                  </div>

                  {/* Import Data */}
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium flex items-center space-x-2">
                          <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
                          <span>Import Transaction History</span>
                        </div>
                        <div className="text-white/70 text-sm">Upload previously exported transaction data</div>
                      </div>
                      <label className="px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors cursor-pointer">
                        Import
                        <input
                          type="file"
                          accept=".json"
                          onChange={handleFileImport}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Clear Data */}
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium flex items-center space-x-2">
                          <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
                          <span>Clear All Data</span>
                        </div>
                        <div className="text-white/70 text-sm">Permanently delete all transaction history</div>
                      </div>
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure? This action cannot be undone.')) {
                            clearTransactions()
                          }
                        }}
                        className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* App Settings */}
              {activeSection === 'app' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-white mb-4">
                    App Settings
                  </h2>

                  {/* Dark Mode */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium flex items-center space-x-2">
                        <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
                        <span>Dark Mode</span>
                      </div>
                      <div className="text-white/70 text-sm">Use dark theme for the app</div>
                    </div>
                    <button
                      onClick={() => setDarkMode(!darkMode)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        darkMode ? 'bg-blue-500' : 'bg-gray-600'
                      }`}
                    >
                      <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
                        darkMode ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  {/* Notifications */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium flex items-center space-x-2">
                        <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
                        <span>Notifications</span>
                      </div>
                      <div className="text-white/70 text-sm">Receive transaction notifications</div>
                    </div>
                    <button
                      onClick={() => setNotifications(!notifications)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        notifications ? 'bg-blue-500' : 'bg-gray-600'
                      }`}
                    >
                      <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
                        notifications ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  {/* Reset Settings */}
                  <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium flex items-center space-x-2">
                          <img src="/Voice-32x32.svg" alt="Voice" className="w-4 h-4" />
                          <span>Reset Settings</span>
                        </div>
                        <div className="text-white/70 text-sm">Reset all settings to default values</div>
                      </div>
                      <button
                        onClick={resetSettings}
                        className="px-4 py-2 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-colors"
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <button
                  onClick={saveSettings}
                  className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPage